<template>
  <div class="event-user-attributes-chart">
    <div class="chart-container">
      <div class="donut-chart">
        <canvas ref="chartCanvas"></canvas>
        <div class="center-text">
          <div class="center-label">{{ $t('dashboard.events.event_reservation_label') }}</div>
          <div class="center-count">{{ eventReservationCount.toLocaleString() }}</div>
          <div class="center-unit">{{ $t('dashboard.events.peopleUnit') }}</div>
        </div>
      </div>
      <div class="user-stats" @click="showDetailModal">
        <h5 class="stats-title">{{ $t('dashboard.events.user_attributes') }}</h5>
        <div class="user-stat-item">
          <span class="label">{{ $t('dashboard.events.stockholder_only') }}</span>
          <div class="bar-container">
            <div class="bar" :style="{ width: stockholderOnlyPercentage + '%', backgroundColor: colors.blue[500] }"></div>
          </div>
          <span class="value">{{ stockholderOnlyPercentage.toFixed(1) }}%</span>
        </div>
        <div class="user-stat-item">
          <span class="label">{{ $t('dashboard.events.repeater_only') }}</span>
          <div class="bar-container">
            <div class="bar" :style="{ width: repeaterOnlyPercentage + '%', backgroundColor: colors.blue[500] }"></div>
          </div>
          <span class="value">{{ repeaterOnlyPercentage.toFixed(1) }}%</span>
        </div>
        <div class="user-stat-item">
          <span class="label">{{ $t('dashboard.events.both') }}</span>
          <div class="bar-container">
            <div class="bar" :style="{ width: bothPercentage + '%', backgroundColor: colors.blue[500] }"></div>
          </div>
          <span class="value">{{ bothPercentage.toFixed(1) }}%</span>
        </div>
        <div class="user-stat-item">
          <span class="label">{{ $t('dashboard.events.other') }}</span>
          <div class="bar-container">
            <div class="bar" :style="{ width: otherPercentage + '%', backgroundColor: colors.blue[500] }"></div>
          </div>
          <span class="value">{{ otherPercentage.toFixed(1) }}%</span>
        </div>
      </div>
    </div>
    <div class="event-capacity">{{ $t('dashboard.events.total_capacity', { count: totalCapacity.toLocaleString() }) }}</div>

    <!-- Detail Modal -->
    <div class="modal fade" id="eventUserAttributesModal" tabindex="-1" aria-labelledby="eventUserAttributesModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="eventUserAttributesModalLabel">{{ $t('dashboard.events.user_attributes') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.events.stockholder_only') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-percentage">{{ stockholderOnlyPercentage.toFixed(1) }}%</span>
              <span class="detail-count">（{{ stockholderOnlyCount.toLocaleString() }}{{ $t('dashboard.events.peopleUnit') }}）</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.events.repeater_only') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-percentage">{{ repeaterOnlyPercentage.toFixed(1) }}%</span>
              <span class="detail-count">（{{ repeaterOnlyCount.toLocaleString() }}{{ $t('dashboard.events.peopleUnit') }}）</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.events.both') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-percentage">{{ bothPercentage.toFixed(1) }}%</span>
              <span class="detail-count">（{{ bothCount.toLocaleString() }}{{ $t('dashboard.events.peopleUnit') }}）</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.events.other') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-percentage">{{ otherPercentage.toFixed(1) }}%</span>
              <span class="detail-count">（{{ otherCount.toLocaleString() }}{{ $t('dashboard.events.peopleUnit') }}）</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import { Modal } from 'bootstrap';
import { colors } from '../../../../consts/colors';

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const { t } = useI18n();

// Computed labels for chart
const chartLabels = computed(() => [
  t('dashboard.events.booked_label'),
  t('dashboard.events.available_label')
]);

const props = defineProps({
  repeaterOnlyRate: {
    type: Number,
    default: 0
  },
  stockholderOnlyRate: {
    type: Number,
    default: 0
  },
  bothRate: {
    type: Number,
    default: 0
  },
  otherRate: {
    type: Number,
    default: 0
  },
  repeaterOnlyPercentage: {
    type: Number,
    default: 0
  },
  stockholderOnlyPercentage: {
    type: Number,
    default: 0
  },
  bothPercentage: {
    type: Number,
    default: 0
  },
  otherPercentage: {
    type: Number,
    default: 0
  },
  repeaterOnlyCount: {
    type: Number,
    default: 0
  },
  stockholderOnlyCount: {
    type: Number,
    default: 0
  },
  bothCount: {
    type: Number,
    default: 0
  },
  otherCount: {
    type: Number,
    default: 0
  },
  totalCapacity: {
    type: Number,
    default: 0
  },
  eventReservationCount: {
    type: Number,
    default: 0
  }
});

const chartCanvas = ref(null);
let chartInstance = null;

// Modal instance
let modalInstance = null;

// Show detail modal
const showDetailModal = () => {
  if (!modalInstance) {
    const modalElement = document.getElementById('eventUserAttributesModal');
    if (modalElement) {
      modalInstance = new Modal(modalElement);
    }
  }
  modalInstance?.show();
};

const initializeChart = () => {
  if (!chartCanvas.value) return;

  const ctx = chartCanvas.value.getContext('2d');

  // Calculate initial participation rate
  const participationRate = props.totalCapacity > 0
    ? (props.eventReservationCount / props.totalCapacity) * 100
    : 0;
  const availableRate = 100 - participationRate;

  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: chartLabels.value,
      datasets: [{
        data: [participationRate, availableRate],
        backgroundColor: [colors.blue[500], colors.gray[100]],
        borderWidth: 0,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              // Don't show tooltip for empty state
              if (!context.label || context.label === '') {
                return null;
              }
              return context.label + ': ' + context.parsed.toFixed(1) + '%';
            }
          },
          filter: function(tooltipItem) {
            // Hide tooltip for empty state
            return tooltipItem.label && tooltipItem.label !== '';
          }
        }
      }
    }
  });
};

const updateChart = () => {
  if (chartInstance) {
    // Calculate participation rate
    const participationRate = props.totalCapacity > 0
      ? (props.eventReservationCount / props.totalCapacity) * 100
      : 0;
    const availableRate = 100 - participationRate;

    // Check if total capacity is 0 (empty state)
    if (props.totalCapacity <= 0) {
      // Show empty state with light gray circle
      chartInstance.data.labels = [''];
      chartInstance.data.datasets[0].data = [1];
      chartInstance.data.datasets[0].backgroundColor = [colors.gray[300]];
      chartInstance.data.datasets[0].borderColor = [colors.gray[200]];
      chartInstance.data.datasets[0].borderWidth = 2;
    } else {
      // Update chart data with actual data
      chartInstance.data.labels = chartLabels.value;
      chartInstance.data.datasets[0].data = [participationRate, availableRate];
      chartInstance.data.datasets[0].backgroundColor = [colors.blue[500], colors.gray[100]];
      chartInstance.data.datasets[0].borderColor = undefined;
      chartInstance.data.datasets[0].borderWidth = 0;
    }

    chartInstance.update();
  }
};

watch(() => [props.eventReservationCount, props.totalCapacity], () => {
  updateChart();
});

onMounted(() => {
  nextTick(() => {
    initializeChart();
    // Initialize chart with current data (including empty state)
    updateChart();
  });
});
</script>

<style lang="scss" scoped>
.event-user-attributes-chart {
  .chart-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    align-items: center;
    margin-bottom: 16px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 24px;
    }

    .donut-chart {
      height: 200px;
      margin-right: auto;
      margin-left: auto;
      position: relative;

      canvas {
        position: relative;
        z-index: 2;
      }

      .center-text {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;
        pointer-events: none;
        z-index: 1;

        .center-label {
          font-size: 12px;
          color: v-bind('colors.gray[500]');
          margin-bottom: 4px;
        }

        .center-count {
          font-size: 32px;
          font-weight: 600;
          color: v-bind('colors.textPrimary');
          line-height: 1;
          margin-bottom: 4px;
        }

        .center-unit {
          font-size: 14px;
          color: v-bind('colors.gray[500]');
        }
      }

      @media (max-width: 768px) {
        height: 250px;
        max-width: 250px;
        margin: 0 auto;
      }
    }

    .user-stats {
      display: flex;
      flex-direction: column;
      gap: 12px;
      cursor: pointer;

      .stats-title {
        font-size: 14px;
        font-weight: 600;
        color: v-bind('colors.gray[500]');
        margin: 0 0 8px;
      }

      .user-stat-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;

        .label {
          flex: 0 0 150px;
          color: v-bind('colors.textPrimary');
        }

        .bar-container {
          flex: 1;
          height: 8px;
          background-color: v-bind('colors.gray[100]');
          border-radius: 4px;
          overflow: hidden;

          .bar {
            height: 100%;
            border-radius: 4px;
            transition: width 0.3s ease;
          }
        }

        .value {
          flex: 0 0 50px;
          text-align: right;
          color: v-bind('colors.gray[500]');
          font-weight: 500;
        }
      }
    }
  }

  .event-capacity {
    font-size: 12px;
    color: v-bind('colors.gray[500]');
    text-align: left;
  }
}

// Modal styles
.modal-content {
  .modal-header {
    background-color: v-bind('colors.white');
    border-bottom: 1px solid v-bind('colors.gray[200]');

    .modal-title {
      font-size: 18px;
      font-weight: 600;
      color: v-bind('colors.textPrimary');
    }
  }

  .modal-body {
    padding: 24px;

    .detail-row {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid v-bind('colors.gray[100]');
      gap: 8px;

      &:last-child {
        border-bottom: none;
      }

      .detail-label {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        text-align: left;
        min-width: 100px;
      }

      .detail-unit {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        white-space: nowrap;
      }

      .detail-percentage {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        text-align: right;
        min-width: 50px;
      }

      .detail-count {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        white-space: nowrap;
      }
    }
  }
}
</style>
